import React, { ReactNode, useState } from 'react';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import MUIAccordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import SvgChevronDown from '../../icons/ChevronDown';
import styled from 'styled-components';
import Text from '../Text/Text';
import { gray } from '../../theme/colors';
import { shadows } from '../../theme/box';

import { MinusLegacy, PlusLegacy } from '../../icons';

export type AccordionProps = {
  /** The title text or node */
  title: ReactNode;

  /** The subtitle text or node */
  subtitle?: ReactNode;

  /** The body content text or node */
  body: ReactNode;

  /** Initially show expanded content or not */
  expanded?: boolean;

  /** badge that appears right of title in laptop and top of title in mobile */
  badge?: ReactNode;

  /** Three variants for Accordion, body 1, body 2 and card */
  accordionVariant?: 'body1' | 'body2' | 'card';

  /** on click function mostly for tracking purpose */
  onClick?: (event: React.MouseEvent<any>) => void;

  /** Boolean value decides current accordion active or not */
  disabled?: boolean;
};

const BadgeContainer = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-width: 414px) {
    flex-direction: column-reverse;
  }
`;

const CardContainer = styled.div`
  && .root {
    box-shadow: none;
    border: 1px solid ${gray[200]};
    border-radius: 8px;

    &:hover {
      border: 1px solid ${gray[300]};
    }

    &:active {
      border: 1px solid ${gray[400]};
    }
  }

  && .disabled {
    background: white;
    pointer-events: none;
  }

  && .summary-root {
    padding: 19px 24px 21px 24px;
  }

  && .summary-content {
    display: block;
    margin: 0;
  }

  && .summary-focused {
    background: white;
    border-radius: 8px;
    box-shadow: ${shadows.focus};
  }

  && .summary-icon {
    transition: none;
  }

  .MuiAccordionSummary-expandIcon.Mui-expanded {
    transform: rotate(180deg) translateY(-5px);
  }

  && .details-root {
    display: block;
    position: relative;
    top: -5px;
    padding: 0 24px 16px 24px;
  }
`;

const RowContainer = styled.div<Partial<AccordionProps>>`
  margin-bottom: ${(props) =>
    props.accordionVariant === 'body1' ? '16px' : '8px'};

  &:last-child {
    margin-bottom: ${(props) =>
      props.accordionVariant === 'body1' ? '24px' : '16px'};
  }

  && .root {
    box-shadow: none;
    border: none;
  }

  && .disabled {
    background: white;
    pointer-events: none;
  }

  && .summary-root {
    min-height: 0;
    padding: 8px;
  }

  && .summary-content {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin: 0;
  }

  && .summary-focused {
    background: white;
    border-radius: 8px;
    box-shadow: inset 0px 0px 0px 2px ${gray[600]};
  }

  && .summary-disabled {
    opacity: 1;
  }

  && .details-root {
    display: block;
    padding: 8px 40px 0 40px;
  }
`;

const Accordion = ({
  title,
  subtitle,
  body,
  disabled,
  badge,
  expanded,
  accordionVariant = 'body1',
  onClick,
}: AccordionProps) => {
  const [expandedState, setExpandedState] = useState(expanded || false);

  if (accordionVariant === 'card') {
    return (
      <CardContainer>
        <MUIAccordion
          classes={{ root: 'root', disabled: 'disabled' }}
          expanded={expandedState}
          disabled={disabled}
          onChange={() => setExpandedState(!expandedState)}
        >
          <AccordionSummary
            classes={{
              content: 'summary-content',
              root: 'summary-root',
              focused: 'summary-focused',
              expandIcon: 'summary-icon',
            }}
            onClick={(event: React.MouseEvent<any>) => {
              if (onClick) onClick(event);
            }}
            expandIcon={<SvgChevronDown />}
          >
            <BadgeContainer>
              <Text variant="subtitle">{title}</Text>
              {badge}
            </BadgeContainer>
            <Text muted>{subtitle}</Text>
          </AccordionSummary>

          <AccordionDetails classes={{ root: 'details-root' }}>
            {typeof body === 'string' ? <Text>{body}</Text> : body}
          </AccordionDetails>
        </MUIAccordion>
      </CardContainer>
    );
  }
  return (
    <RowContainer accordionVariant={accordionVariant}>
      <MUIAccordion
        classes={{ root: 'root', disabled: 'disabled' }}
        expanded={expandedState}
        disabled={disabled}
        onChange={() => setExpandedState(!expandedState)}
      >
        <AccordionSummary
          classes={{
            root: 'summary-root',
            content: 'summary-content',
            focused: 'summary-focused',
            disabled: 'summary-disabled',
          }}
          onClick={(event: React.MouseEvent<any>) => {
            if (onClick) onClick(event);
          }}
        >
          {expandedState ? (
            <MinusLegacy
              textColor={disabled ? 'gray-300' : 'crunchberry'}
              size="12px"
            />
          ) : (
            <PlusLegacy
              textColor={disabled ? 'gray-300' : 'crunchberry'}
              size="12px"
            />
          )}
          <Text
            variant={accordionVariant === 'body1' ? 'subtitle' : 'subtitle2'}
            color={disabled ? gray['300'] : ''}
            as="span"
            paddingLeft={'20px'}
          >
            {title}
          </Text>
        </AccordionSummary>
        <AccordionDetails classes={{ root: 'details-root' }}>
          {typeof body === 'string' ? (
            <Text variant={accordionVariant === 'body1' ? 'body' : 'body2'}>
              {body}
            </Text>
          ) : (
            body
          )}
        </AccordionDetails>
      </MUIAccordion>
    </RowContainer>
  );
};

Accordion.displayName = 'Accordion';
export default Accordion;
